/**
 * TenantContactDetails
 *
 * De kolom **Contactgegevens** van de contractentabellen: e-mailadres en
 * telefoonnummer van de huurder(s) van één huurcontract.
 *
 * Aanleiding (BUG-608): *"Wat ons opvalt, is dat deze gegevens niet automatisch
 * worden overgenomen naar de verhuurpagina van het gebouw. Daar blijven de velden
 * leeg staan."*
 *
 * De cel werd opgebouwd als
 * `(contact?.relatedContact?.email + ", " || "") + contact?.relatedContact?.phone_mobile || ""`.
 * Drie dingen liepen daar mis:
 *
 * 1. `undefined + ", "` is de **tekst** `"undefined, "` — en die is truthy, dus
 *    de `|| ""` erachter deed niets. Een huurder zonder e-mailadres las als
 *    "undefined, 0499…".
 * 2. Ontbrak ook het telefoonnummer, dan stond er `"undefined, undefined"`.
 * 3. En hing er helemaal geen contactfiche aan het contract, dan bleef de cel
 *    **leeg** — zonder te zeggen dat er niets te tonen viel. Precies de klacht
 *    hierboven: leeg leest als "de gegevens komen niet mee", terwijl het even
 *    goed "deze huurder hangt aan geen contactfiche" kan betekenen. Dat zijn twee
 *    verschillende problemen met twee verschillende oplossingen.
 *
 * De gegevens komen **live** uit de contactfiche (de backend zoekt ze bij elke
 * lading opnieuw op via de resource), dus een aangevuld telefoonnummer staat er
 * meteen. Wie hier toch "geen contactgegevens" ziet, kijkt naar een fiche waar
 * ze effectief nog niet ingevuld zijn; wie "niet gekoppeld" ziet, moet de
 * huurder aan zijn contactfiche koppelen.
 */

import { Envelope, Phone, WarningCircle } from "@phosphor-icons/react";

export interface TenantContactLike {
  id?: number;
  /** De contactfiche achter deze contractant; `null` wanneer die ontbreekt. */
  relatedContact?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone_mobile?: string | null;
    phone_landline?: string | null;
  } | null;
  /** De resource (naamdrager) van de contractant, als terugval voor de naam. */
  resource?: { name?: string | null } | null;
}

interface TenantContactDetailsProps {
  /** De contactanten van het contract, al gefilterd op huurder. */
  contacts: TenantContactLike[];
}

const naamVan = (contact: TenantContactLike): string => {
  const fiche = contact.relatedContact;
  const volledigeNaam = [fiche?.firstName, fiche?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return volledigeNaam || contact.resource?.name?.trim() || "Onbekend";
};

export const TenantContactDetails = ({
  contacts,
}: TenantContactDetailsProps) => {
  if (!contacts || contacts.length === 0) {
    return <span className="text-neutral-400">—</span>;
  }

  const meerdere = contacts.length > 1;

  return (
    <div className="flex flex-col gap-1.5">
      {contacts.map((contact, index) => {
        const fiche = contact.relatedContact;
        const telefoon = fiche?.phone_mobile || fiche?.phone_landline || null;
        const email = fiche?.email || null;

        return (
          <div
            key={contact.id ?? index}
            className="flex flex-col text-xs leading-tight"
          >
            {meerdere && (
              <span className="text-neutral-secondary-mid font-medium">
                {naamVan(contact)}
              </span>
            )}

            {!fiche ? (
              <span
                className="flex items-center gap-1 text-amber-700"
                title="Deze contractant hangt aan geen contactfiche, dus er zijn geen gegevens om over te nemen"
              >
                <WarningCircle size={12} weight="fill" />
                Niet gekoppeld aan een contactfiche
              </span>
            ) : !email && !telefoon ? (
              <span
                className="text-neutral-400"
                title="De contactfiche heeft nog geen e-mailadres of telefoonnummer"
              >
                Geen contactgegevens
              </span>
            ) : (
              <>
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="text-moss-primary flex items-center gap-1 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Envelope size={12} weight="duotone" />
                    <span className="truncate">{email}</span>
                  </a>
                )}
                {telefoon && (
                  <a
                    href={`tel:${telefoon.replace(/\s/g, "")}`}
                    className="text-neutral-primary flex items-center gap-1 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone size={12} weight="duotone" />
                    {telefoon}
                  </a>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
